import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type {
  BdBehandlerart,
} from '@bindoc-projects/clinic-domain-lib';
import {
  BD_BEHANDLERART_DISPLAY_NAME
} from '@bindoc-projects/clinic-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const BD_BEHANDLERART_CONDITION_ID = 'behandlerart';

export const FilterConditionBehandlerartType: FilterConditionPluginSpecification<
  BdBehandlerart[],
  FilterConditionBehandlerartDto,
  BdBehandlerartCondition
> = {
  type: 'behandlerart',
  category: 'person',
  displayName: { de: 'Behandlerart' },
  sortingIndex: 10,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionBehandlerartDto,
  ): FilterConditionBehandlerartPlugin {
    return new FilterConditionBehandlerartPlugin(
      (obj.includes ?? []) as BdBehandlerart[],
    );
  },
  fromCondition(
    jsonObj: BdBehandlerartCondition,
  ): FilterConditionBehandlerartPlugin {
    return new FilterConditionBehandlerartPlugin(jsonObj?.includes ?? []);
  },
  newInstance(): FilterConditionBehandlerartPlugin {
    return new FilterConditionBehandlerartPlugin([]);
  },
};

export class FilterConditionBehandlerartPlugin extends FilterConditionPluginImpl<
  BdBehandlerart[],
  FilterConditionBehandlerartDto
> {
  public static override Specification = FilterConditionBehandlerartType;

  constructor(private includes: BdBehandlerart[]) {
    super(FilterConditionBehandlerartType.type);
  }

  override clone(): FilterConditionBehandlerartPlugin {
    return new FilterConditionBehandlerartPlugin([...this.includes]);
  }

  override equals(other: FilterConditionBehandlerartPlugin): boolean {
    return (
      super.equals(other) && BdArrayUtil.equals(this.includes, other.includes)
    );
  }

  override validationToText(): string {
    if (this.includes.length === 0) {
      return 'Mindestens eine Behandlerart auswählen';
    } else {
      return '';
    }
  }

  override toCondition(): BdCondition {
    return new BdBehandlerartCondition(this.includes, []);
  }

  override toJSON(): FilterConditionBehandlerartDto {
    return {
      type: this.type,
      includes: this.includes,
    };
  }

  override toText(): string {
    return this.includes.length === 1
      ? 'eine Behandlerart'
      : `${this.includes.length} Behandlerarten`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    return {
      title,
      items: this.includes.map((id) => BD_BEHANDLERART_DISPLAY_NAME[id] ?? id),
    };
  }

  override toValue(): BdBehandlerart[] {
    return this.includes;
  }
}

class BdBehandlerartCondition extends BdContentCondition<BdBehandlerart> {
  constructor(includes: BdBehandlerart[], excludes: BdBehandlerart[]) {
    super(BD_BEHANDLERART_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionBehandlerartDto {
  type: string;
  includes: string[];
}
